import axios, { HttpStatusCode } from "axios";

import {
  API_BASE,
  API_BASE_LANDINGS,
  AUTH_SESSION_REFRESH_PATH,
  RoutesApiConst,
} from "../../constants";
import { IAccessRefreshToken } from "../../models";
import { apiBaseUrl, isLandingsApiApp } from "../env.helpers";
import { Log } from "../log.helpers";
import {
  normalizeUrlBasePath,
  storeCurrentPageAndRedirectToLogin,
} from "../url.helpers";
import {
  getRefreshToken,
  isLoginPage,
  setRefreshToken,
  updateTokensAndReconnectSocket,
} from "./auth.helpers";
import {
  readBrowserCookie,
  SESSION_CSRF_COOKIE_NAME,
  SESSION_CSRF_HEADER_NAME,
} from "./session-cookie.constants";
import { logTokenExpiration } from "./token-logging";

let isRefreshing = false;
let pendingRequests: {
  resolve: () => void;
  reject: (reason?: unknown) => void;
}[] = [];

export const getTokenIsRefreshing = (): boolean => isRefreshing;

export const refreshAccessToken = async (): Promise<void> => {
  const oldIsRefreshing = isRefreshing;
  isRefreshing = true;

  if (oldIsRefreshing) {
    return new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
    });
  }

  try {
    await doRefreshToken();
    pendingRequests.forEach(req => req.resolve());
    pendingRequests = [];
  } catch (error) {
    Log.warn("Refresh token failed", error);
    setRefreshToken(null);
    pendingRequests.forEach(req => req.reject(error));
    pendingRequests = [];

    if (!isLoginPage()) {
      Log.debug("Error refreshing token, navigating to login");

      storeCurrentPageAndRedirectToLogin();
    }
  } finally {
    isRefreshing = false;
  }
};

const doRefreshToken = async (): Promise<void> => {
  const legacyRefresh = getRefreshToken();

  if (legacyRefresh) {
    await doRefreshTokenLegacyBearer(legacyRefresh);
    return;
  }

  await doRefreshTokenSessionCookies();
};

const doRefreshTokenLegacyBearer = async (
  oldRefreshToken: string,
): Promise<void> => {
  logTokenExpiration(oldRefreshToken, "EXPIRED TOKEN REFRESH");

  const response = await axios.post<{ data: IAccessRefreshToken }>(
    RoutesApiConst.auth.refreshBothTokens,
    null,
    {
      baseURL: apiBaseUrl(),
      headers: {
        Authorization: `Bearer ${oldRefreshToken}`,
      },
    },
  );

  const { access_token: accessToken, refresh_token: refreshToken } =
    response.data.data;

  if (!accessToken || !refreshToken) {
    throw new Error("Invalid tokens in refresh response");
  }

  Log.debug("Expired token refreshed and refreshed in pending call");
  updateTokensAndReconnectSocket({ accessToken, refreshToken });
};

const doRefreshTokenSessionCookies = async (): Promise<void> => {
  const csrf = readBrowserCookie(SESSION_CSRF_COOKIE_NAME);
  const headers: Record<string, string> = {};

  if (csrf) {
    headers[SESSION_CSRF_HEADER_NAME] = csrf;
  }

  const appApiBase = isLandingsApiApp() ? API_BASE_LANDINGS : API_BASE;
  const url = `${normalizeUrlBasePath(apiBaseUrl() as string)}${appApiBase}${AUTH_SESSION_REFRESH_PATH}`;

  const response = await axios.post(url, null, {
    withCredentials: true,
    headers,
    validateStatus: status =>
      status === HttpStatusCode.NoContent || status === HttpStatusCode.Ok,
  });

  if (
    response.status !== HttpStatusCode.NoContent &&
    response.status !== HttpStatusCode.Ok
  ) {
    throw new Error("Invalid session refresh response");
  }

  Log.debug("Session cookies refreshed after 401");
  updateTokensAndReconnectSocket({ accessToken: null, refreshToken: null });
};
